// src/pages/finance-portal/inventory/Products.js
//
// Product Catalog list page.
//
// Supports search, status / type / category / low-stock filters,
// pagination, CSV bulk import, and quick-open to ProductDetail.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert, Autocomplete, Avatar, Box, Button, Checkbox, Chip,
    CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, FormControlLabel, IconButton, InputAdornment, InputLabel,
    MenuItem, Paper, Select, Skeleton, Stack, Table, TableBody, TableCell,
    TableContainer, TableHead, TablePagination, TableRow, TextField,
    Tooltip, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import axiosInstance from '../../../utils/axiosInstance';
import {
    FONT_FAMILY, INV_ACCENT, INV_ACCENT_DARK,
    formatCurrency, formatQty, formatDateTime,
    PRODUCT_STATUS_COLORS, PRODUCT_TYPE_COLORS, statusChip,
} from './components/inventoryFormatters';

const STATUS_OPTIONS = ['', 'ACTIVE', 'DRAFT', 'DISCONTINUED', 'ARCHIVED'];
const TYPE_OPTIONS = ['', 'INVENTORY', 'NON_INVENTORY', 'SERVICE', 'BUNDLE', 'DIGITAL'];

export default function Products() {
    const navigate = useNavigate();

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(25);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [categoryFilter, setCategoryFilter] = useState(null);
    const [lowStockOnly, setLowStockOnly] = useState(false);
    const [categories, setCategories] = useState([]);

    const [deleteDialog, setDeleteDialog] = useState({ open: false, product: null });
    const [importDialog, setImportDialog] = useState({ open: false, file: null, busy: false, result: null });

    const searchTimer = useRef(null);

    const fetchProducts = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {
                page: page + 1, per_page: perPage,
                include_variants: false, include_stock: true,
            };
            if (search) params.search = search;
            if (statusFilter) params.status = statusFilter;
            if (typeFilter) params.product_type = typeFilter;
            if (categoryFilter) params.category_id = categoryFilter.id;
            if (lowStockOnly) params.low_stock = true;

            const res = await axiosInstance.get('/api/finance/inventory/products', { params });
            if (res.data?.success) {
                setRows(res.data.data || []);
                setTotal(res.data.total || 0);
            } else {
                setError(res.data?.message || 'Failed to load');
            }
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load products');
        } finally {
            setLoading(false);
        }
    }, [page, perPage, search, statusFilter, typeFilter, categoryFilter, lowStockOnly]);

    useEffect(() => { fetchProducts(); }, [fetchProducts]);

    useEffect(() => {
        axiosInstance.get('/api/finance/inventory/categories')
            .then((r) => setCategories(r.data?.data || []))
            .catch(() => {});
    }, []);

    const handleSearchChange = (v) => {
        if (searchTimer.current) clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => {
            setPage(0);
            setSearch(v);
        }, 300);
    };

    const handleDelete = async () => {
        if (!deleteDialog.product) return;
        try {
            await axiosInstance.delete(`/api/finance/inventory/products/${deleteDialog.product.id}`);
            setDeleteDialog({ open: false, product: null });
            fetchProducts();
        } catch (e) {
            alert(e.response?.data?.message || 'Delete failed');
        }
    };

    const handleImport = async () => {
        if (!importDialog.file) return;
        setImportDialog((d) => ({ ...d, busy: true, result: null }));
        try {
            const fd = new FormData();
            fd.append('file', importDialog.file);
            const res = await axiosInstance.post('/api/finance/inventory/products/import-csv', fd);
            setImportDialog((d) => ({ ...d, busy: false, result: res.data }));
            fetchProducts();
        } catch (e) {
            setImportDialog((d) => ({ ...d, busy: false, result: { success: false, message: e.response?.data?.message || 'Import failed' } }));
        }
    };

    return (
        <Box sx={{ p: 3, fontFamily: FONT_FAMILY, bgcolor: '#f8fafc', minHeight: '100vh' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                <Box>
                    <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '1.65rem', fontWeight: 700, color: '#0f172a' }}>
                        Products &amp; Services
                    </Typography>
                    <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.9rem', color: '#64748b', mt: 0.5 }}>
                        SKU catalog, variants, pricing, and GL mappings
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Button
                        variant="outlined" startIcon={<UploadFileIcon />}
                        onClick={() => setImportDialog({ open: true, file: null, busy: false, result: null })}
                        sx={{ fontFamily: FONT_FAMILY, textTransform: 'none', borderColor: '#cbd5e1', color: '#334155' }}
                    >
                        Import CSV
                    </Button>
                    <Button
                        variant="contained" startIcon={<AddIcon />}
                        onClick={() => navigate('/finance-portal/inventory/products/new')}
                        sx={{ fontFamily: FONT_FAMILY, textTransform: 'none', bgcolor: INV_ACCENT, '&:hover': { bgcolor: INV_ACCENT_DARK } }}
                    >
                        Add Product
                    </Button>
                </Stack>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 2, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                    <TextField
                        size="small" fullWidth placeholder="Search SKU, name, barcode, MPN…"
                        onChange={(e) => handleSearchChange(e.target.value)}
                        sx={{ fontFamily: FONT_FAMILY, flex: 1, minWidth: 280 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#94a3b8' }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                        <InputLabel>Status</InputLabel>
                        <Select value={statusFilter} label="Status" onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
                            {STATUS_OPTIONS.map((s) => <MenuItem key={s} value={s}>{s || 'All'}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Type</InputLabel>
                        <Select value={typeFilter} label="Type" onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}>
                            {TYPE_OPTIONS.map((t) => <MenuItem key={t} value={t}>{t || 'All'}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <Autocomplete
                        size="small" options={categories}
                        getOptionLabel={(o) => o.name || ''}
                        value={categoryFilter}
                        onChange={(e, v) => { setCategoryFilter(v); setPage(0); }}
                        sx={{ minWidth: 200 }}
                        renderInput={(params) => <TextField {...params} label="Category" />}
                    />
                    <FormControlLabel
                        control={<Checkbox checked={lowStockOnly}
                                    onChange={(e) => { setLowStockOnly(e.target.checked); setPage(0); }} />}
                        label={<Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.88rem' }}>Low stock only</Typography>}
                    />
                </Stack>
            </Paper>

            {/* Table */}
            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>SKU</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Name</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Type</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Price</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Cost</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">On Hand</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Value</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Status</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                Array.from({ length: 6 }).map((_, i) => (
                                    <TableRow key={i}>
                                        <TableCell colSpan={9}><Skeleton height={32} /></TableCell>
                                    </TableRow>
                                ))
                            ) : rows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} sx={{ py: 6, textAlign: 'center', color: '#64748b' }}>
                                        No products match the current filters
                                    </TableCell>
                                </TableRow>
                            ) : (
                                rows.map((p) => (
                                    <TableRow
                                        key={p.id} hover
                                        onClick={() => navigate(`/finance-portal/inventory/products/${p.id}`)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem', fontWeight: 600 }}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Avatar
                                                    src={p.images?.[0]?.url}
                                                    sx={{ width: 32, height: 32, bgcolor: '#e0f2fe', color: INV_ACCENT }}
                                                >
                                                    <Inventory2Icon fontSize="small" />
                                                </Avatar>
                                                <Box>
                                                    <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem', fontWeight: 600 }}>
                                                        {p.sku}
                                                    </Typography>
                                                    {p.barcode && (
                                                        <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.72rem', color: '#64748b' }}>
                                                            {p.barcode}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Stack>
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.88rem' }}>
                                            {p.name}
                                            {p.category_name && (
                                                <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.72rem', color: '#64748b' }}>
                                                    {p.category_name}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small" label={p.product_type?.replace('_', ' ')}
                                                style={statusChip(p.product_type, PRODUCT_TYPE_COLORS)}
                                                sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, fontSize: '0.7rem' }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem' }} align="right">
                                            {formatCurrency(p.unit_price, p.currency)}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem', color: '#64748b' }} align="right">
                                            {formatCurrency(p.average_cost || p.unit_cost, p.currency)}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem' }} align="right">
                                            {p.is_stock_tracked ? (
                                                <Typography sx={{
                                                    fontFamily: FONT_FAMILY, fontSize: '0.85rem',
                                                    fontWeight: p.locations_low > 0 ? 700 : 500,
                                                    color: p.locations_low > 0 ? '#dc2626' : '#0f172a',
                                                }}>
                                                    {formatQty(p.total_on_hand || 0)}
                                                    {p.locations_low > 0 && (
                                                        <Typography component="span" sx={{ fontSize: '0.7rem', color: '#dc2626', ml: 0.5 }}>
                                                            ⚠
                                                        </Typography>
                                                    )}
                                                </Typography>
                                            ) : (
                                                <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.72rem', color: '#94a3b8' }}>
                                                    not tracked
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem', fontWeight: 600 }} align="right">
                                            {p.is_stock_tracked ? formatCurrency(p.total_value || 0, p.currency) : '—'}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small" label={p.status}
                                                style={statusChip(p.status, PRODUCT_STATUS_COLORS)}
                                                sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, fontSize: '0.7rem' }}
                                            />
                                        </TableCell>
                                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                            <Tooltip title="Edit">
                                                <IconButton size="small" onClick={() => navigate(`/finance-portal/inventory/products/${p.id}/edit`)}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton size="small"
                                                    onClick={() => setDeleteDialog({ open: true, product: p })}>
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div" count={total} page={page}
                    onPageChange={(e, p) => setPage(p)}
                    rowsPerPage={perPage}
                    onRowsPerPageChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    sx={{ '.MuiTablePagination-toolbar': { fontFamily: FONT_FAMILY } }}
                />
            </Paper>

            {/* Delete dialog */}
            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, product: null })}>
                <DialogTitle sx={{ fontFamily: FONT_FAMILY }}>Delete product?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily: FONT_FAMILY }}>
                        Delete <b>{deleteDialog.product?.sku}</b> — <i>{deleteDialog.product?.name}</i>?
                    </Typography>
                    <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.82rem', color: '#64748b', mt: 1 }}>
                        If the product has inventory movement history, it will be archived instead.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog({ open: false, product: null })}
                            sx={{ fontFamily: FONT_FAMILY, textTransform: 'none' }}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained"
                            sx={{ fontFamily: FONT_FAMILY, textTransform: 'none' }}>Delete</Button>
                </DialogActions>
            </Dialog>

            {/* Import dialog */}
            <Dialog open={importDialog.open} onClose={() => setImportDialog({ open: false, file: null, busy: false, result: null })}
                    maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily: FONT_FAMILY }}>Bulk CSV Import</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem', color: '#64748b', mb: 2 }}>
                        Required header columns: <code>sku, name</code>. Optional:{' '}
                        <code>description, product_type, unit_price, unit_cost, barcode, mpn, unit_of_measure,
                        reorder_point, reorder_quantity, valuation_method, tax_code, track_lots,
                        track_serials, preferred_vendor_id, category_id</code>.
                    </Typography>
                    <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}
                            sx={{ fontFamily: FONT_FAMILY, textTransform: 'none' }}>
                        {importDialog.file ? importDialog.file.name : 'Choose CSV file'}
                        <input type="file" hidden accept=".csv,text/csv"
                               onChange={(e) => setImportDialog((d) => ({ ...d, file: e.target.files[0] || null, result: null }))} />
                    </Button>
                    {importDialog.result && (
                        <Alert severity={importDialog.result.success ? 'success' : 'error'} sx={{ mt: 2 }}>
                            {importDialog.result.success
                                ? `Created: ${importDialog.result.created}, Updated: ${importDialog.result.updated}${
                                    importDialog.result.errors?.length ? `, Errors: ${importDialog.result.errors.length}` : ''}`
                                : (importDialog.result.message || 'Import failed')}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setImportDialog({ open: false, file: null, busy: false, result: null })}
                            sx={{ fontFamily: FONT_FAMILY, textTransform: 'none' }}>Close</Button>
                    <Button onClick={handleImport} disabled={!importDialog.file || importDialog.busy}
                            variant="contained"
                            sx={{ fontFamily: FONT_FAMILY, textTransform: 'none', bgcolor: INV_ACCENT, '&:hover': { bgcolor: INV_ACCENT_DARK } }}>
                        {importDialog.busy ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Import'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
